import React from 'react';
import { FaMapPin, FaPhoneAlt, FaWhatsapp } from "react-icons/fa";
import { IoIosMail } from "react-icons/io";
import { Link } from 'react-router-dom';
import { serviceList } from '../../constants/services';
import { trackContactClick, trackOutbound } from '../../utils/analytics';

const FindUs = () => {
  return (
    <footer data-nosnippet className="bg-gradient-to-b from-primary to-primary/95 text-white pt-8 sm:pt-12 pb-0 px-4 w-full relative overflow-hidden">
      {/* Background gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-[500px] h-[500px] rounded-full bg-gradient-to-r from-accent/30 to-accent/0 blur-3xl -top-[250px] -left-[250px]" />
        <div className="absolute w-[500px] h-[500px] rounded-full bg-gradient-to-r from-accent/20 to-accent/0 blur-3xl -bottom-[250px] -right-[250px]" />
      </div>
      <div className="relative">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 sm:gap-10 pb-6 sm:pb-8">
          {/* Brand Column */}
          <div className="flex flex-col gap-4 sm:gap-6">
            <div className="w-[120px] sm:w-[150px] mx-auto sm:mx-0">
              <img
                src="/assets/logowdf-orig-removebg.webp"
                alt="WDF - software development company in Dubai"
                className="w-full h-auto brightness-0 invert"
                loading="lazy"
                decoding="async"
              />
            </div>
            <h2 className="text-1xl sm:text-2xl leading-tight m-0 text-center sm:text-left font-base">
              Digital Solutions That Drive Growth
            </h2>
            <p className="text-sm text-white/60 leading-relaxed text-center sm:text-left">
              {'WDF is a Dubai-based software company building websites, mobile apps, AI automation, custom operations software and embedded systems for clients worldwide.'}
            </p>
          </div>

          {/* Services Column - every service page linked from every page on the
              site, so none of them depends on the nav dropdown being crawled. */}
          <div>
            <h3 className="text-xl mb-6 relative pb-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-[50px] after:h-[2px] after:bg-accent/30">
              Services
            </h3>
            <ul className="list-none p-0 m-0 flex flex-col gap-3">
              {serviceList.map((service) => (
                <li key={service.slug}>
                  <Link
                    to={service.path}
                    className="text-white/80 no-underline transition-colors duration-300 hover:text-white text-base cursor-pointer"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div>
            <h3 className="text-xl mb-6 relative pb-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-[50px] after:h-[2px] after:bg-accent/30">
              Explore
            </h3>
            <ul className="list-none p-0 m-0 flex flex-col gap-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'All Services', path: '/services' },
                { name: 'Our Work', path: '/projects' },
                { name: 'Software Development in Dubai', path: '/software-development-dubai' },
                { name: 'Insights', path: '/blogs' },
                { name: 'Contact', path: '/contact' },
              ].map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className="text-white/80 no-underline transition-colors duration-300 hover:text-white text-base cursor-pointer"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social Media Column */}
          <div>
            <h3 className="text-xl mb-6 relative pb-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-[50px] after:h-[2px] after:bg-accent/30">
              Connect With Us
            </h3>
            <div className="flex gap-4 flex-wrap">
              <a href="https://www.instagram.com/webdevf/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" onClick={() => trackOutbound('https://www.instagram.com/webdevf')}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-white/5 border border-white/10 transition-all duration-300 hover:bg-white/10 hover:border-white/20 group">
                <img
                  src="/assets/logos/instagram.svg"
                  alt="Instagram"
                  className="w-6 h-6 object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </a>
              <a href="https://www.facebook.com/profile.php?id=61578703775977" target="_blank" rel="noopener noreferrer" aria-label="Facebook" onClick={() => trackOutbound('https://www.facebook.com/profile.php?id=61578703775977')}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-white/5 border border-white/10 transition-all duration-300 hover:bg-white/10 hover:border-white/20 group">
                <img
                  src="/assets/logos/facebook.svg"
                  alt="Facebook"
                  className="w-6 h-6 object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </a>
              {/* The YouTube icon linked to youtube.com - the site's homepage,
                  not a WDF channel - on all thirteen pages. An outbound link to
                  a destination that says nothing about us is worth less than no
                  link. Restore it pointing at a real channel URL when one
                  exists. */}
            </div>
          </div>

          {/* Contact Info Column */}
          <div>
            <h3 className="text-xl mb-6 relative pb-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-[50px] after:h-[2px] after:bg-accent/30">
              Contact Info
            </h3>
            <address className="not-italic">
              <p className="flex items-center gap-2 mb-3">
                <span className="text-lg"><FaMapPin /></span>
                <span>Dubai, United Arab Emirates</span>
              </p>
              <p className="flex items-center gap-2 mb-3">
                <span className="text-lg"><FaPhoneAlt /></span>
                <a
                  href="tel:+971524818924"
                  onClick={() => trackContactClick('phone', 'footer')}
                  className="text-white/80 hover:text-white transition-colors duration-300"
                >
                  +971 52 481 8924
                </a>
              </p>
              <p className="flex items-center gap-2 mb-3">
                <span className="text-lg"><FaWhatsapp /></span>
                <a
                  href="https://wa.me/971524818924"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackContactClick('whatsapp', 'footer')}
                  className="text-white/80 hover:text-white transition-colors duration-300"
                >
                  WhatsApp
                </a>
              </p>
              <p className="flex items-center gap-2 mb-3">
                <span className="text-lg"><IoIosMail size={22} /></span>
                <a
                  href="mailto:contact@webdevf.com"
                  onClick={() => trackContactClick('email', 'footer')}
                  className="text-white/80 hover:text-white transition-colors duration-300"
                >
                  contact@webdevf.com
                </a>
              </p>
            </address>
          </div>
        </div>

        {/* Copyright */}
        <div className="relative border-t border-white/10 py-4 text-center">
          <p>{`\u00A9 ${new Date().getFullYear()} WDF. All rights reserved.`}</p>
        </div>
      </div>
    </footer>
  );
};

export default FindUs;